import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Redirect } from 'react-router-dom';
import axios from '../../axios/axiosconf';
import Header from '../general/Header';
import Aside from '../general/Aside';
import Footer from '../general/Footer';
import Loading from '../general/Loading';
import '../../../resources/css/suscripcion-smile.css';

const DOC_TYPES = ['CC', 'CE', 'NIT', 'PP'];

const accessStateLabel = (estado) => {
    switch (Number(estado)) {
        case 0: return 'Activo';
        case 1: return 'Bloqueado';
        case 3: return 'Pendiente de pago';
        case 2: return 'Retirado';
        default: return '—';
    }
};

const PasswordInput = ({
    id,
    name,
    value,
    onChange,
    disabled,
    placeholder,
    autoComplete,
    className = 'form-control',
}) => {
    const [visible, setVisible] = useState(false);

    return (
        <div className="input-group">
            <input
                id={id}
                name={name}
                type={visible ? 'text' : 'password'}
                className={className}
                value={value}
                onChange={onChange}
                disabled={disabled}
                placeholder={placeholder}
                autoComplete={autoComplete}
            />
            <div className="input-group-append">
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setVisible((v) => !v)}
                    disabled={disabled}
                    aria-label={visible ? 'Ocultar' : 'Mostrar'}
                    tabIndex={-1}
                >
                    <i className={`fas fa-eye${visible ? '-slash' : ''}`} />
                </button>
            </div>
        </div>
    );
};

const SwField = ({ label, icon, hint, children, htmlFor }) => (
    <div className="sw-field form-group">
        <label className="sw-field-label" htmlFor={htmlFor}>
            {icon && <i className={`fas ${icon} sw-field-icon`} aria-hidden="true" />}
            {label}
        </label>
        {children}
        {hint && <small className="sw-field-hint">{hint}</small>}
    </div>
);

const PaymentCardFields = ({ prefix, form, onChange, disabled }) => (
    <>
        <SwField label="Número de tarjeta" icon="fa-credit-card" htmlFor={`${prefix}_card_number`}>
            <div className="sw-card-number-wrap">
                <input
                    id={`${prefix}_card_number`}
                    name="card_number"
                    className="form-control"
                    autoComplete="cc-number"
                    placeholder="1234 1234 1234 1234"
                    value={form.card_number}
                    onChange={onChange}
                    required
                    disabled={disabled}
                />
                <div className="sw-card-brands" aria-hidden="true">
                    <span>VISA</span>
                    <span>MC</span>
                    <span>AMEX</span>
                </div>
            </div>
        </SwField>
        <div className="row sw-exp-row">
            <div className="col-4">
                <SwField label="Mes (MM)" icon="fa-calendar-alt" htmlFor={`${prefix}_card_exp_month`}>
                    <input
                        id={`${prefix}_card_exp_month`}
                        name="card_exp_month"
                        className="form-control"
                        placeholder="MM"
                        maxLength={2}
                        value={form.card_exp_month}
                        onChange={onChange}
                        required
                        disabled={disabled}
                    />
                </SwField>
            </div>
            <div className="col-4">
                <SwField label="Año (AAAA)" icon="fa-calendar-alt" htmlFor={`${prefix}_card_exp_year`}>
                    <input
                        id={`${prefix}_card_exp_year`}
                        name="card_exp_year"
                        className="form-control"
                        placeholder="AAAA"
                        maxLength={4}
                        value={form.card_exp_year}
                        onChange={onChange}
                        required
                        disabled={disabled}
                    />
                </SwField>
            </div>
            <div className="col-4">
                <SwField label="Código de seguridad (CVV)" icon="fa-lock" htmlFor={`${prefix}_card_cvc`}>
                    <PasswordInput
                        id={`${prefix}_card_cvc`}
                        name="card_cvc"
                        value={form.card_cvc}
                        onChange={onChange}
                        disabled={disabled}
                        placeholder="123"
                        autoComplete="cc-csc"
                    />
                </SwField>
            </div>
        </div>
    </>
);

const toInputDateValue = (value) => {
    if (!value) return '';
    return String(value).slice(0, 10);
};

const MESES_ES = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

/** Fecha de cobro en español: "5 de septiembre de 2026". */
const formatFechaCobroEs = (value) => {
    const ymd = toInputDateValue(value);
    if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
        return null;
    }
    const [y, m, d] = ymd.split('-').map((n) => parseInt(n, 10));
    if (!y || !m || !d || m < 1 || m > 12) {
        return ymd;
    }
    return `${d} de ${MESES_ES[m - 1]} de ${y}`;
};

/** Mensaje post-inscripción (sin mencionar ePayco). */
const mensajeSuscripcionInscrita = (fechaCobro) => {
    const fechaTexto = formatFechaCobroEs(fechaCobro) || 'día de cobro programado';
    return (
        'Suscripción registrada correctamente en SmileWeb.\n\n'
        + `Tu medio de pago ya quedó inscrito y programado. El primer débito automático se realizará el ${fechaTexto}.\n\n`
        + 'Mientras tu servicio se encuentre vigente, no necesitas realizar ningún pago adicional ni proceso de regularización.'
    );
};

const todayInputDate = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const initialForm = {
    fecha_suscripcion: todayInputDate(),
    name: '',
    last_name: '',
    email: '',
    doc_type: 'CC',
    doc_number: '',
    phone: '',
    card_number: '',
    card_exp_month: '',
    card_exp_year: '',
    card_cvc: '',
};

function ParametrosSuscripcionEpayco({ embedded = false }) {
    const [ready, setReady] = useState(false);
    const [client, setClient] = useState([]);
    const [permisos, setPermisos] = useState([]);
    const [subscription, setSubscription] = useState(null);
    const [form, setForm] = useState(initialForm);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [charging, setCharging] = useState(false);
    const [retryingPayment, setRetryingPayment] = useState(false);
    const [updatingPaymentMethod, setUpdatingPaymentMethod] = useState(false);
    const [showChangePaymentForm, setShowChangePaymentForm] = useState(false);
    const [paymentMethodForm, setPaymentMethodForm] = useState({
        doc_type: 'CC',
        doc_number: '',
        card_number: '',
        card_exp_month: '',
        card_exp_year: '',
        card_cvc: '',
    });
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('info');
    const [debugPayload, setDebugPayload] = useState(null);
    const [acceptTerms, setAcceptTerms] = useState(false);

    const syncFormDateFromRules = useCallback((subData, prevForm) => {
        const allowed = subData?.subscription_date_rules?.allowed_dates;
        if (!Array.isArray(allowed) || allowed.length === 0) {
            return toInputDateValue(subData?.fecha_suscripcion) || prevForm.fecha_suscripcion || todayInputDate();
        }
        const dates = allowed.map((row) => row.date);
        const stored = toInputDateValue(subData?.fecha_suscripcion);
        const current = prevForm.fecha_suscripcion || stored || todayInputDate();
        if (dates.includes(current)) {
            return current;
        }
        // Si el día de cobro guardado ya pasó este mes, preferir ese mismo día el próximo mes.
        if (stored) {
            const day = Number(String(stored).slice(8, 10));
            const sameDayNext = dates.find((d) => Number(String(d).slice(8, 10)) === day && d > stored);
            if (sameDayNext) {
                return sameDayNext;
            }
        }
        // Con periodo vigente, no forzar "débito hoy" si hay fechas futuras.
        const deferred = allowed.find((row) => row.date && !row.charge_immediately);
        if (deferred?.date) {
            return deferred.date;
        }
        return dates[0];
    }, []);

    const canManage = useMemo(
        () => permisos.includes('configuracion')
            || permisos.includes('administrarParametrosSistema')
            || permisos.includes('ver_suscripcion'),
        [permisos]
    );

    const loadData = useCallback(async (opts = {}) => {
        const isAlive = () => !opts.cancelled?.current;
        setLoading(true);
        setMessage('');

        const userdataRaw = localStorage.getItem('userdata');
        if (!userdataRaw) {
            if (isAlive()) {
                setReady(true);
                setLoading(false);
            }
            return;
        }

        const userdata = JSON.parse(userdataRaw);
        let permisosList = [];
        if (userdata.permissions && Array.isArray(userdata.permissions)) {
            permisosList = userdata.permissions.map((p) => p.slug);
        } else if (userdata.up) {
            permisosList = userdata.up;
        }
        if (isAlive()) {
            setPermisos(permisosList);
        }

        try {
            const [clientRes, subRes] = await Promise.all([
                axios.get('/get-client?uid=' + userdata.user_id),
                axios.get('/epayco/subscription', {
                    headers: { 'X-Suppress-403-Modal': '1' },
                }),
            ]);

            if (!isAlive()) {
                return;
            }

            const subData = subRes.status === 200 ? subRes.data : null;

            if (clientRes.status === 200) {
                setClient(clientRes.data);
            }

            if (subData) {
                setSubscription(subData);
                setForm((prev) => ({
                    ...initialForm,
                    fecha_suscripcion: syncFormDateFromRules(subData, prev),
                }));
            }
        } catch (err) {
            if (!isAlive()) {
                return;
            }
            const apiMsg = err.response?.data?.message;
            setMessageType('danger');
            setMessage(apiMsg || 'No se pudo cargar el estado de suscripción ePayco.');
        } finally {
            if (isAlive()) {
                setReady(true);
                setLoading(false);
            }
        }
    }, [syncFormDateFromRules]);

    useEffect(() => {
        const cancelled = { current: false };
        loadData({ cancelled });
        return () => {
            cancelled.current = true;
        };
    }, [loadData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleActivate = async (e) => {
        e.preventDefault();
        if (!canManage) {
            return;
        }
        if (!acceptTerms) {
            setMessageType('warning');
            setMessage('Debe aceptar los términos y condiciones para continuar.');
            return;
        }

        setSubmitting(true);
        setMessage('');
        setDebugPayload(null);
        try {
            const res = await axios.post('/epayco/subscription/activate', form, {
                headers: { 'X-Suppress-403-Modal': '1' },
            });
            const sub = res.data?.subscription || {};
            const rejected = sub.charge_status === 'rejected';
            const deferred = !sub.charge_approved && !sub.immediate_debit && sub.charge_deferred_reason;
            const enrolledOk = Boolean(sub.epayco_subscription_id) && !rejected && !sub.charge_approved;
            setMessageType(rejected ? 'warning' : deferred || enrolledOk ? 'info' : 'success');
            if ((deferred || enrolledOk) && !rejected) {
                setMessage(mensajeSuscripcionInscrita(
                    sub.fecha_suscripcion || form.fecha_suscripcion
                ));
            } else {
                setMessage(res.data?.message || 'Suscripción activada.');
            }
            setDebugPayload(res.data?.debug || res.data);
            setForm((prev) => ({
                ...initialForm,
                fecha_suscripcion: syncFormDateFromRules(subscription, prev),
            }));
            await loadData();
        } catch (err) {
            setMessageType('danger');
            const data = err.response?.data;
            const detailsText = data?.details
                ? (typeof data.details === 'string'
                    ? data.details
                    : JSON.stringify(data.details, null, 2))
                : '';
            setMessage(
                detailsText
                    ? `${data?.message || 'Error al activar la suscripción.'}\n${detailsText}`
                    : data?.message || 'Error al activar la suscripción.'
            );
            if (data?.debug || data) {
                setDebugPayload(data.debug || data);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handlePauseDebit = async () => {
        if (
            !canManage
            || !window.confirm(
                '¿Pausar el débito automático?\n\n'
                + 'La suscripción ePayco se conserva (no se cancela). '
                + 'Si el acceso se bloquea, podrá reintentar el cobro desde el login con la misma fecha de cobro.\n\n'
                + 'Para cancelación definitiva en ePayco debe contactar soporte SmileWEB.'
            )
        ) {
            return;
        }

        setCancelling(true);
        setMessage('');
        try {
            const res = await axios.post('/epayco/subscription/pause', {}, {
                headers: { 'X-Suppress-403-Modal': '1' },
            });
            setMessageType('success');
            setMessage(res.data?.message || 'Débito automático pausado.');
            await loadData();
        } catch (err) {
            setMessageType('danger');
            setMessage(err.response?.data?.message || 'No se pudo pausar el débito automático.');
        } finally {
            setCancelling(false);
        }
    };

    const handleResetLinks = async () => {
        if (
            !canManage
            || !window.confirm(
                '¿Limpiar customer y suscripción en Smile?\n\n'
                + 'Cancela la suscripción en ePayco (si existe) y borra los IDs locales.\n'
                + 'Al activar de nuevo se creará un customer nuevo.\n'
                + 'Los customers viejos quedan en el panel ePayco (no se pueden borrar por API).'
            )
        ) {
            return;
        }

        setResetting(true);
        setMessage('');
        setDebugPayload(null);
        try {
            const res = await axios.post('/epayco/subscription/reset-links', {}, {
                headers: { 'X-Suppress-403-Modal': '1' },
            });
            setMessageType('success');
            setMessage(res.data?.message || 'Vínculos limpiados.');
            if (res.data?.reset) {
                setDebugPayload(res.data.reset);
            }
            await loadData();
        } catch (err) {
            setMessageType('danger');
            setMessage(err.response?.data?.message || 'No se pudieron limpiar los vínculos.');
            if (err.response?.data?.details) {
                setDebugPayload({ details: err.response.data.details });
            }
        } finally {
            setResetting(false);
        }
    };

    const handleRetrySubscriptionCharge = async () => {
        if (!canManage || processingPaymentLocked()) {
            return;
        }
        if (!window.confirm('¿Reintentar el débito con la tarjeta registrada en ePayco?')) {
            return;
        }

        setRetryingPayment(true);
        setMessage('');
        setDebugPayload(null);
        try {
            const res = await axios.post('/epayco/subscription/retry-charge', {}, {
                headers: { 'X-Suppress-403-Modal': '1' },
            });
            applyChargeResponse(res);
        } catch (err) {
            applyChargeError(err);
        } finally {
            setRetryingPayment(false);
        }
    };

    const processingPaymentLocked = () => retryingPayment || updatingPaymentMethod || charging || submitting;

    const applyChargeResponse = (res) => {
        const rejected = res.data?.charge?.charge_status === 'rejected'
            || res.data?.debug?.summary?.charge_status === 'rejected';
        setMessageType(rejected ? 'warning' : 'success');
        setMessage(res.data?.message || 'Operación completada.');
        setDebugPayload(res.data?.debug || res.data);
    };

    const applyChargeError = (err) => {
        setMessageType('danger');
        const data = err.response?.data;
        setMessage(
            data?.details
                ? `${data?.message || 'Error.'}\n${typeof data.details === 'string' ? data.details : JSON.stringify(data.details, null, 2)}`
                : data?.message || 'No se pudo completar la operación.'
        );
        if (data?.debug || data) {
            setDebugPayload(data.debug || data);
        }
    };

    const handleUpdatePaymentMethod = async () => {
        if (!canManage || processingPaymentLocked()) {
            return;
        }

        setUpdatingPaymentMethod(true);
        setMessage('');
        setDebugPayload(null);
        try {
            const res = await axios.post('/epayco/subscription/update-payment-method', {
                ...paymentMethodForm,
                doc_type: paymentMethodForm.doc_type || form.doc_type,
                doc_number: paymentMethodForm.doc_number || form.doc_number,
            }, {
                headers: { 'X-Suppress-403-Modal': '1' },
            });
            applyChargeResponse(res);
            setShowChangePaymentForm(false);
            setPaymentMethodForm({
                doc_type: form.doc_type || 'CC',
                doc_number: form.doc_number || '',
                card_number: '',
                card_exp_month: '',
                card_exp_year: '',
                card_cvc: '',
            });
            await loadData();
        } catch (err) {
            applyChargeError(err);
        } finally {
            setUpdatingPaymentMethod(false);
        }
    };

    const handlePaymentMethodFieldChange = (e) => {
        const { name, value } = e.target;
        setPaymentMethodForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleCharge = async () => {
        if (!canManage || processingPaymentLocked() || !window.confirm('¿Ejecutar el débito inicial ePayco hoy? Solo puede hacerse una vez.')) {
            return;
        }

        setCharging(true);
        setMessage('');
        setDebugPayload(null);
        try {
            const res = await axios.post('/epayco/subscription/charge', {
                fecha_suscripcion: form.fecha_suscripcion || null,
            }, {
                headers: { 'X-Suppress-403-Modal': '1' },
            });
            applyChargeResponse(res);
            await loadData();
        } catch (err) {
            applyChargeError(err);
        } finally {
            setCharging(false);
        }
    };

    if (!ready) {
        return <Loading />;
    }

    if (!localStorage.getItem('userdata')) {
        return <Redirect to="/" />;
    }

    const hasSubscription = subscription?.has_active_subscription;
    const hasEnrolledSubscription = Boolean(subscription?.epayco_subscription_id);
    const isDebitPaused = Boolean(subscription?.debito_automatico_pausado);
    const isTest = subscription?.test;
    const cancelledNotice = subscription?.subscription_cancelled_notice;
    const pendingFirstCharge = subscription?.subscription_pending_first_charge
        || (
            subscription?.epayco_subscription_id
            && !subscription?.fecha_primer_pago
            && subscription?.epayco_subscription_status === 'inactive'
        );
    const canResubscribe = subscription?.can_resubscribe !== false;
    const needsSupportResubscribe = subscription?.needs_support_resubscribe;
    const dateRules = subscription?.subscription_date_rules;
    const allowedDates = dateRules?.allowed_dates || [];
    const selectedDateRule = allowedDates.find((row) => row.date === form.fecha_suscripcion);
    const willChargeOnActivate = Boolean(
        selectedDateRule?.charge_immediately
        || subscription?.charge_immediately_on_activate
        || form.fecha_suscripcion === todayInputDate()
    );
    const paymentProcessing = retryingPayment || updatingPaymentMethod || charging || submitting;
    const isPendingAccess = Number(subscription?.estado) === 3;
    const isBlockedAccess = Number(subscription?.estado) === 1;
    const showUrgentPaymentActions = hasEnrolledSubscription && (
        isPendingAccess || isBlockedAccess
    );

    const subscriptionBadge = isDebitPaused
        ? { label: 'Pausada', className: 'badge-secondary' }
        : hasSubscription
            ? { label: 'Activa', className: 'badge-success' }
            : pendingFirstCharge
                ? { label: 'Inscrita (inactiva)', className: 'badge-warning' }
                : cancelledNotice
                    ? { label: 'Débito cancelado', className: 'badge-warning' }
                    : { label: 'Sin activar', className: 'sw-badge-soft' };

    const subscriptionBody = (
                    <div className={embedded ? '' : 'container-fluid'}>
                        {!embedded && (
                        <div className="sw-sub-topbar">
                            <ol className="breadcrumb mb-0 bg-transparent p-0">
                                <li className="breadcrumb-item"><Link to="/home">Home</Link></li>
                                <li className="breadcrumb-item"><Link to="/settings">Configuración</Link></li>
                                <li className="breadcrumb-item"><Link to="/parametros-sistema">Parámetros</Link></li>
                                <li className="breadcrumb-item active">Suscripción</li>
                            </ol>
                            <Link to="/parametros-sistema" className="btn btn-outline-secondary btn-sm" style={{ borderRadius: '10px', fontWeight: 600 }}>
                                <i className="fas fa-arrow-left mr-1" /> Volver
                            </Link>
                        </div>
                        )}

                        {!embedded && (
                        <div className="sw-sub-hero">
                            <h1>Suscribe tu método de pago</h1>
                            <p>Rápido, seguro y sin complicaciones</p>
                        </div>
                        )}
                        {isTest && (
                            <div className="alert alert-warning">
                                <strong>Modo pruebas ePayco.</strong> Use tarjetas de sandbox. Ejemplo Visa:{' '}
                                <code>4575623182290326</code> · exp <code>12/2028</code> · CVC <code>123</code>
                            </div>
                        )}

                        {cancelledNotice && !pendingFirstCharge && (
                            <div className="alert alert-warning">
                                <strong>Su débito automático fue cancelado en ePayco.</strong>{' '}
                                Si el acceso está bloqueado, comuníquese con soporte SmileWEB para registrar una nueva suscripción.
                            </div>
                        )}

                        {needsSupportResubscribe && (
                            <div className="alert alert-warning">
                                <strong>Sin suscripción en ePayco.</strong>{' '}
                                Soporte canceló el débito definitivamente. Comuníquese con la oficina SmileWEB para una nueva inscripción
                                (el día de cobro será el de la reactivación).
                            </div>
                        )}

                        {isDebitPaused && (
                            <div className="alert alert-secondary">
                                <strong>Débito automático pausado.</strong>{' '}
                                La suscripción ePayco sigue registrada. Si el acceso se bloquea, use «Reintentar cobro» o «Cambiar tarjeta y pagar»
                                desde el login. Para cancelación definitiva en ePayco, contacte soporte.
                            </div>
                        )}

                        {pendingFirstCharge && (
                            <div className="alert alert-info" style={{ whiteSpace: 'pre-wrap' }}>
                                {isPendingAccess || isBlockedAccess
                                    ? (
                                        <>
                                            <strong>Suscripción registrada en SmileWeb.</strong>{' '}
                                            Aún no hay débito exitoso. Puede reintentar el pago o cambiar la tarjeta.
                                        </>
                                    )
                                    : mensajeSuscripcionInscrita(subscription?.fecha_suscripcion)}
                            </div>
                        )}

                        {message && (
                            <div className={`alert alert-${messageType}`} style={{ whiteSpace: 'pre-wrap' }}>{message}</div>
                        )}

                        {debugPayload && isTest && (
                            <div className="alert alert-secondary">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <strong>Debug ePayco (copiar y pegar)</strong>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-dark"
                                        onClick={() => {
                                            const text = JSON.stringify(debugPayload, null, 2);
                                            if (navigator.clipboard?.writeText) {
                                                navigator.clipboard.writeText(text);
                                            }
                                        }}
                                    >
                                        Copiar JSON
                                    </button>
                                </div>
                                <pre
                                    style={{
                                        maxHeight: 320,
                                        overflow: 'auto',
                                        fontSize: 12,
                                        marginBottom: 0,
                                        background: '#f8f9fa',
                                        padding: 12,
                                        borderRadius: 4,
                                    }}
                                >
                                    {JSON.stringify(debugPayload, null, 2)}
                                </pre>
                            </div>
                        )}

                        {!subscription?.configured && (
                            <div className="alert alert-danger">
                                ePayco no está configurado en el servidor (credenciales .env).
                            </div>
                        )}

                        <div className="row">
                            <div className="col-lg-8 mb-4">
                                <div className="sw-pay-card">
                                    <div className="sw-pay-card-head">
                                        <div className="sw-pay-card-icon">
                                            <i className="fas fa-credit-card" />
                                        </div>
                                        <div>
                                            <h2>Información de pago</h2>
                                            <small>
                                                {hasEnrolledSubscription
                                                    ? 'Actualice su tarjeta o gestione el débito automático.'
                                                    : 'Completa los datos para suscribir tu método de pago.'}
                                            </small>
                                        </div>
                                    </div>
                                    <div className="sw-pay-card-body">
                                        {!canManage && (
                                            <div className="alert alert-info mb-3">
                                                Solo usuarios con permiso de configuración pueden activar la suscripción.
                                            </div>
                                        )}

                                        {hasEnrolledSubscription ? (
                                            <>
                                                <p className="text-muted mb-3">
                                                    Su medio de pago ya está inscrito. Si el acceso se bloquea, también puede regularizar desde el login.
                                                </p>
                                                {canManage && subscription?.can_update_payment_method && (
                                                    <>
                                                        {!showChangePaymentForm && (
                                                            <button
                                                                type="button"
                                                                className="sw-btn-primary"
                                                                onClick={() => setShowChangePaymentForm(true)}
                                                                disabled={paymentProcessing}
                                                            >
                                                                <i className="fas fa-sync-alt mr-2" />
                                                                {subscription?.charge_after_payment_method_update
                                                                    ? 'Cambiar tarjeta y pagar'
                                                                    : 'Cambiar método de pago'}
                                                            </button>
                                                        )}
                                                        {showChangePaymentForm && (
                                                            <div className="mt-2">
                                                                <div className="row">
                                                                    <div className="col-md-4">
                                                                        <SwField label="Tipo documento" icon="fa-id-card" htmlFor="enrolled_doc_type">
                                                                            <select
                                                                                id="enrolled_doc_type"
                                                                                name="doc_type"
                                                                                className="form-control custom-select"
                                                                                value={paymentMethodForm.doc_type}
                                                                                onChange={handlePaymentMethodFieldChange}
                                                                                disabled={paymentProcessing}
                                                                            >
                                                                                {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                                                                            </select>
                                                                        </SwField>
                                                                    </div>
                                                                    <div className="col-md-8">
                                                                        <SwField label="Documento de identificación" icon="fa-id-card" htmlFor="enrolled_doc_number">
                                                                            <input
                                                                                id="enrolled_doc_number"
                                                                                name="doc_number"
                                                                                className="form-control"
                                                                                placeholder="Cédula o NIT"
                                                                                value={paymentMethodForm.doc_number}
                                                                                onChange={handlePaymentMethodFieldChange}
                                                                                disabled={paymentProcessing}
                                                                            />
                                                                        </SwField>
                                                                    </div>
                                                                </div>
                                                                <PaymentCardFields
                                                                    prefix="enrolled"
                                                                    form={paymentMethodForm}
                                                                    onChange={handlePaymentMethodFieldChange}
                                                                    disabled={paymentProcessing}
                                                                />
                                                                <div className="d-flex flex-wrap">
                                                                    <button
                                                                        type="button"
                                                                        className="sw-btn-primary flex-grow-1 mr-2 mb-2"
                                                                        onClick={handleUpdatePaymentMethod}
                                                                        disabled={paymentProcessing}
                                                                    >
                                                                        <i className="fas fa-lock mr-2" />
                                                                        {updatingPaymentMethod ? 'Procesando…' : (
                                                                            subscription?.charge_after_payment_method_update
                                                                                ? 'Actualizar tarjeta y pagar'
                                                                                : 'Guardar nueva tarjeta'
                                                                        )}
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-secondary"
                                                                        onClick={() => setShowChangePaymentForm(false)}
                                                                        disabled={paymentProcessing}
                                                                    >
                                                                        Cancelar
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </>
                                        ) : needsSupportResubscribe ? (
                                            <div className="text-center py-4">
                                                <i className="fas fa-headset fa-2x text-muted mb-3" />
                                                <p className="text-muted mb-0">
                                                    Comuníquese con soporte SmileWEB para reactivar el débito automático.
                                                </p>
                                            </div>
                                        ) : (
                                            <form onSubmit={handleActivate} autoComplete="off">
                                                {dateRules?.help && (
                                                    <div className="alert alert-light border small mb-3 py-2">
                                                        {dateRules.help}
                                                    </div>
                                                )}

                                                <SwField label="Nombre del titular" icon="fa-user" htmlFor="name" hint="Nombre completo como aparece en la tarjeta.">
                                                    <input
                                                        id="name"
                                                        name="name"
                                                        className="form-control"
                                                        value={form.name}
                                                        onChange={handleChange}
                                                        autoComplete="off"
                                                        placeholder="Nombre completo"
                                                        required
                                                        disabled={!canManage || submitting}
                                                    />
                                                </SwField>

                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <SwField label="Apellido del titular" icon="fa-user" htmlFor="last_name">
                                                            <input
                                                                id="last_name"
                                                                name="last_name"
                                                                className="form-control"
                                                                value={form.last_name}
                                                                onChange={handleChange}
                                                                autoComplete="off"
                                                                placeholder="Apellido"
                                                                disabled={!canManage || submitting}
                                                            />
                                                        </SwField>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <SwField label="Celular (10 dígitos)" icon="fa-mobile-alt" htmlFor="phone">
                                                            <input
                                                                id="phone"
                                                                name="phone"
                                                                type="tel"
                                                                className="form-control"
                                                                value={form.phone}
                                                                onChange={handleChange}
                                                                disabled={!canManage || submitting}
                                                                placeholder="3001234567"
                                                                autoComplete="off"
                                                                required
                                                            />
                                                        </SwField>
                                                    </div>
                                                </div>

                                                <SwField label="Correo electrónico" icon="fa-envelope" htmlFor="email">
                                                    <input
                                                        id="email"
                                                        name="email"
                                                        type="email"
                                                        className="form-control"
                                                        value={form.email}
                                                        onChange={handleChange}
                                                        autoComplete="off"
                                                        placeholder="ejemplo@correo.com"
                                                        required
                                                        disabled={!canManage || submitting}
                                                    />
                                                </SwField>

                                                <PaymentCardFields
                                                    prefix="activate"
                                                    form={form}
                                                    onChange={handleChange}
                                                    disabled={!canManage || submitting}
                                                />

                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <SwField label="Fecha de pago" icon="fa-calendar-alt" htmlFor="fecha_suscripcion">
                                                            {allowedDates.length > 0 ? (
                                                                <select
                                                                    id="fecha_suscripcion"
                                                                    name="fecha_suscripcion"
                                                                    className="form-control custom-select"
                                                                    value={form.fecha_suscripcion}
                                                                    onChange={handleChange}
                                                                    required
                                                                    disabled={!canManage || submitting}
                                                                >
                                                                    {allowedDates.map((row) => (
                                                                        <option key={row.date} value={row.date}>
                                                                            {row.date}
                                                                            {row.charge_immediately ? ' — débito hoy' : ''}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            ) : (
                                                                <input
                                                                    id="fecha_suscripcion"
                                                                    name="fecha_suscripcion"
                                                                    type="date"
                                                                    className="form-control"
                                                                    value={form.fecha_suscripcion}
                                                                    onChange={handleChange}
                                                                    required
                                                                    disabled={!canManage || submitting}
                                                                />
                                                            )}
                                                            <small className="sw-field-hint">
                                                                {willChargeOnActivate
                                                                    ? 'Con esta fecha se envía el débito al activar.'
                                                                    : 'El débito se ejecutará en la fecha seleccionada.'}
                                                            </small>
                                                        </SwField>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <SwField label="País de facturación" icon="fa-globe-americas">
                                                            <input className="form-control" value="Colombia" readOnly disabled />
                                                        </SwField>
                                                    </div>
                                                </div>

                                                <div className="row">
                                                    <div className="col-md-4">
                                                        <SwField label="Tipo documento" icon="fa-id-card" htmlFor="doc_type">
                                                            <select
                                                                id="doc_type"
                                                                name="doc_type"
                                                                className="form-control custom-select"
                                                                value={form.doc_type}
                                                                onChange={handleChange}
                                                                disabled={!canManage || submitting}
                                                            >
                                                                {DOC_TYPES.map((t) => (
                                                                    <option key={t} value={t}>{t}</option>
                                                                ))}
                                                            </select>
                                                        </SwField>
                                                    </div>
                                                    <div className="col-md-8">
                                                        <SwField label="Documento de identificación" icon="fa-id-card" htmlFor="doc_number">
                                                            <input
                                                                id="doc_number"
                                                                name="doc_number"
                                                                className="form-control"
                                                                value={form.doc_number}
                                                                onChange={handleChange}
                                                                autoComplete="off"
                                                                placeholder="Cédula o NIT"
                                                                required
                                                                disabled={!canManage || submitting}
                                                            />
                                                        </SwField>
                                                    </div>
                                                </div>

                                                <div className="sw-secure-box">
                                                    <i className="fas fa-shield-alt" />
                                                    <p>
                                                        <strong>Pago 100% seguro.</strong>{' '}
                                                        Tu información está protegida con encriptación de nivel bancario.
                                                        Los datos de tarjeta se tokenizan en ePayco; Smile no almacena el número completo.
                                                    </p>
                                                </div>

                                                <div className="custom-control custom-checkbox mb-3">
                                                    <input
                                                        type="checkbox"
                                                        className="custom-control-input"
                                                        id="accept_terms"
                                                        checked={acceptTerms}
                                                        onChange={(e) => setAcceptTerms(e.target.checked)}
                                                        disabled={!canManage || submitting}
                                                    />
                                                    <label className="custom-control-label small" htmlFor="accept_terms">
                                                        Acepto los Términos y Condiciones y la Política de Privacidad de SmileWeb.
                                                    </label>
                                                </div>

                                                <button
                                                    type="submit"
                                                    className="sw-btn-primary"
                                                    disabled={!canManage || submitting || !subscription?.configured || !canResubscribe}
                                                >
                                                    <i className="fas fa-lock mr-2" />
                                                    {submitting ? 'Procesando…' : 'Suscribir método de pago'}
                                                </button>
                                                <p className="sw-cancel-note">Puedes cancelar en cualquier momento.</p>
                                            </form>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-4 mb-4">
                                <div className="sw-sidebar">
                                    <div className="sw-sidebar-illus">
                                        <div className="sw-sidebar-illus-inner">
                                            <i className="fas fa-lock" />
                                        </div>
                                    </div>

                                    <div className="sw-benefits">
                                        <h3>¿Por qué suscribir un método de pago?</h3>
                                        <div className="sw-benefit-item">
                                            <i className="fas fa-bolt" />
                                            <div>
                                                <strong>Activación inmediata</strong>
                                                <span>Empiece a usar SmileWeb sin demoras manuales en el cobro.</span>
                                            </div>
                                        </div>
                                        <div className="sw-benefit-item">
                                            <i className="fas fa-sync" />
                                            <div>
                                                <strong>Renovación automática</strong>
                                                <span>Su plan se renueva automáticamente para no perder el acceso.</span>
                                            </div>
                                        </div>
                                        <div className="sw-benefit-item">
                                            <i className="fas fa-shield-alt" />
                                            <div>
                                                <strong>Total seguridad</strong>
                                                <span>Información protegida con estándares de alta seguridad.</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="sw-plan-card">
                                        <h4><i className="fas fa-file-invoice-dollar" /> Resumen de tu plan</h4>
                                        {loading ? (
                                            <p className="text-muted mb-0">Cargando…</p>
                                        ) : subscription ? (
                                            <>
                                                <div className="sw-plan-row">
                                                    <span>Plan</span>
                                                    <span>{subscription.periodicidad_label || subscription.tipo_contrato || 'Smile WEB'}</span>
                                                </div>
                                                <div className="sw-plan-row">
                                                    <span>Suscripción</span>
                                                    <span>
                                                        <span className={`badge ${subscriptionBadge.className}`}>
                                                            {subscriptionBadge.label}
                                                        </span>
                                                    </span>
                                                </div>
                                                <div className="sw-plan-row">
                                                    <span>Fecha de pago</span>
                                                    <span>
                                                        {subscription.fecha_suscripcion
                                                            ? toInputDateValue(subscription.fecha_suscripcion)
                                                            : (subscription.charge_day ? `Día ${subscription.charge_day}` : '—')}
                                                    </span>
                                                </div>
                                                <div className="sw-plan-row">
                                                    <span>Próximo vencimiento</span>
                                                    <span>
                                                        {subscription.fecha_vencimiento
                                                            ? toInputDateValue(subscription.fecha_vencimiento)
                                                            : '—'}
                                                    </span>
                                                </div>
                                                {subscription.fecha_bloqueo && (
                                                    <div className="sw-plan-row">
                                                        <span>Fecha de bloqueo</span>
                                                        <span>{toInputDateValue(subscription.fecha_bloqueo)}</span>
                                                    </div>
                                                )}
                                                <div className="sw-plan-row">
                                                    <span>Estado de acceso</span>
                                                    <span className={isBlockedAccess ? 'text-danger' : isPendingAccess ? 'text-warning' : ''}>
                                                        {accessStateLabel(subscription.estado)}
                                                    </span>
                                                </div>
                                                <div className="sw-plan-total">
                                                    <span className="sw-plan-total-label">Total Por Ciclo</span>
                                                    <span className="sw-plan-total-value">
                                                        ${Number(subscription.amount || 0).toLocaleString('es-CO')} COP
                                                    </span>
                                                </div>
                                                {isTest && subscription.epayco_plan_id && (
                                                    <small className="text-muted d-block mt-2">
                                                        Plan (prueba): <code>{subscription.epayco_plan_id}</code>
                                                    </small>
                                                )}
                                            </>
                                        ) : (
                                            <p className="text-muted mb-0">Sin datos de suscripción.</p>
                                        )}
                                    </div>

                                    <div className="sw-actions-stack mt-3">
                                        {showUrgentPaymentActions && (
                                            <div className="alert alert-warning py-2 small mb-2">
                                                <strong>Regularice su servicio.</strong>{' '}
                                                {isPendingAccess
                                                    ? 'Su cuenta está pendiente de pago.'
                                                    : isBlockedAccess
                                                        ? 'Su cuenta está bloqueada.'
                                                        : 'Puede reintentar el débito con su tarjeta registrada.'}
                                            </div>
                                        )}

                                        {canManage && subscription?.can_retry_charge && (
                                            <button
                                                type="button"
                                                className={`btn sw-btn-primary btn-block mb-2${showUrgentPaymentActions ? ' font-weight-bold' : ''}`}
                                                onClick={handleRetrySubscriptionCharge}
                                                disabled={paymentProcessing}
                                            >
                                                {retryingPayment ? 'Procesando cobro…' : 'Reintentar pago'}
                                            </button>
                                        )}
                                        {canManage && subscription && !subscription.can_retry_charge && subscription.retry_charge_reason && (
                                            <small className="text-muted d-block mb-2">
                                                Reintento: {subscription.retry_charge_reason}
                                            </small>
                                        )}

                                        {canManage && isTest && subscription?.can_manual_charge && (
                                            <button
                                                type="button"
                                                className="btn btn-success btn-block mb-2"
                                                onClick={handleCharge}
                                                disabled={paymentProcessing}
                                            >
                                                {charging ? 'Cobrando…' : 'Cargar pago (débito inicial)'}
                                            </button>
                                        )}

                                        {hasEnrolledSubscription && !isDebitPaused && canManage && (
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary btn-block mb-2"
                                                onClick={handlePauseDebit}
                                                disabled={cancelling || resetting}
                                            >
                                                {cancelling ? 'Pausando…' : 'Pausar débito automático'}
                                            </button>
                                        )}

                                        {isTest && canManage && (
                                            <button
                                                type="button"
                                                className="btn btn-outline-warning btn-block btn-sm"
                                                onClick={handleResetLinks}
                                                disabled={resetting || cancelling}
                                            >
                                                {resetting ? 'Limpiando…' : 'Limpiar customer (prueba)'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
    );

    if (embedded) {
        return (
            <div className="smile-subscription-page" style={{ fontSize: '13px' }}>
                {subscriptionBody}
            </div>
        );
    }

    return (
        <div className="wrapper hold-transition smile-subscription-page" style={{ fontSize: '13px' }}>
            <Header client={client} />
            <Aside client={client} activelink="settings" />
            <div className="content-wrapper">
                <section className="content pt-3">
                    {subscriptionBody}
                </section>
            </div>
            <Footer showToast={false} delay={0} />
        </div>
    );
}

export default ParametrosSuscripcionEpayco;
